import { getFirebaseAuth } from '@/lib/firebase';
import {
  AuthErrorCodes,
  getMultiFactorResolver,
  IdTokenResult,
  MultiFactorInfo,
  PhoneMultiFactorGenerator,
  signInWithEmailAndPassword,
  UserCredential,
} from '@firebase/auth';
import { FirebaseError } from '@firebase/app';

export const getErrorMessage = (error: FirebaseError) => {
  switch (error.code) {
    case AuthErrorCodes.INVALID_EMAIL:
    case AuthErrorCodes.USER_DELETED:
    case AuthErrorCodes.INVALID_PASSWORD:
      return 'メールアドレスまたはパスワードが正しくありません。連続してログインに失敗するとログインが制限されます。';
    case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
      return '連続して不正なパスワードが入力されたため、一時的にログインを制限しました。しばらくしてから再度ログインをお試しください。';
    case AuthErrorCodes.INVALID_LOGIN_CREDENTIALS:
      return 'メールアドレスまたはパスワードが正しくありません。';
    default:
      return 'エラーが発生しました';
  }
};

export const login = async ({ email, password }: { email: string; password: string }) => {
  if (location.hostname === 'localhost') {
    try {
      const authEntity = await loginWithPasswordWithoutMultiFactor({
        email,
        password,
      });

      return authEntity;
    } catch (e) {
      throw e;
    }
  } else {
    try {
      const [authEntity, multiFactorHints] = await loginWithPassword({
        email,
        password,
      });
      const phoneMultiFactorHints = multiFactorHints.filter(hint => {
        return hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID;
      });
      if (phoneMultiFactorHints.length > 0) {
        // TODO: 認証コードの送信画面に遷移
        return;
      }

      return authEntity;
    } catch (e) {
      throw e;
    }
  }
};

async function loginWithPasswordWithoutMultiFactor({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<{ idToken: IdTokenResult; userCredential: UserCredential }> {
  const userCredential = await signInWithEmailAndPassword(
    getFirebaseAuth(),
    email.toLowerCase(),
    password
  );
  let idToken = await userCredential.user.getIdTokenResult();
  if (!idToken.claims?.aim_user_id) {
    idToken = await userCredential.user.getIdTokenResult(true);
  }
  return {
    idToken,
    userCredential,
  };
}

async function loginWithPassword({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<
  [{ idToken: IdTokenResult; userCredential: UserCredential } | undefined, MultiFactorInfo[]]
> {
  return new Promise(async (resolve, reject) => {
    await signInWithEmailAndPassword(getFirebaseAuth(), email.toLowerCase(), password)
      .then(async userCredential => {
        if (userCredential.user) {
          let idToken = await userCredential.user.getIdTokenResult();
          if (!idToken.claims?.aim_user_id) {
            idToken = await userCredential.user.getIdTokenResult(true);
          }
          resolve([
            {
              idToken,
              userCredential,
            },
            [],
          ]);
        }
      })
      .catch(error => {
        if (error.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(getFirebaseAuth(), error);
          return resolve([undefined, resolver?.hints || []]);
        }
        return reject(error);
      });
  });
}
